import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  FileText, 
  Download, 
  Eye, 
  EyeOff,
  Calendar,
  Building,
  User,
  DollarSign,
  Clock,
  TrendingUp,
  Minus,
  Info
} from 'lucide-react';
import { format } from 'date-fns';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import type { PayrollPreviewResult } from '@/services/compensation/types';

interface PayslipPreviewCardProps {
  employeeData: PayrollPreviewResult;
  payPeriodStart: Date;
  payPeriodEnd: Date;
  showSensitiveData?: boolean;
  onExport?: (format: 'pdf' | 'csv') => void;
}

export function PayslipPreviewCard({
  employeeData,
  payPeriodStart,
  payPeriodEnd,
  showSensitiveData = true,
  onExport,
}: PayslipPreviewCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDetails, setShowDetails] = useState(showSensitiveData);
  const { currencyCode } = useWorkplaceCurrency();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const formatHours = (hours: number) => {
    return hours.toFixed(2);
  };

  const getEmployeeInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const groupLineItemsByCategory = () => {
    const grouped = {
      earnings: [] as any[],
      deductions: [] as any[],
      taxes: [] as any[],
      benefits: [] as any[]
    };

    employeeData.calculation.lineItems.forEach(item => {
      if (item.category === 'earnings') {
        grouped.earnings.push(item);
      } else if (item.category === 'taxes') {
        // Direct tax category from calculation engine
        grouped.taxes.push(item);
      } else if (item.category === 'deductions') {
        if (item.description.toLowerCase().includes('tax')) {
          grouped.taxes.push(item);
        } else {
          grouped.deductions.push(item);
        }
      }
    });

    return grouped;
  };

  const lineItemGroups = groupLineItemsByCategory();
  const totalEarnings = lineItemGroups.earnings.reduce((sum, item) => sum + item.gross_amount, 0);
  const totalDeductions = lineItemGroups.deductions.reduce((sum, item) => sum + item.gross_amount, 0);
  const totalTaxes = lineItemGroups.taxes.reduce((sum, item) => sum + item.gross_amount, 0);

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Avatar className="h-12 w-12">
              <AvatarImage src="" />
              <AvatarFallback className="bg-primary/10 text-primary">
                {getEmployeeInitials(employeeData.employeeName)}
              </AvatarFallback>
            </Avatar>
            
            <div>
              <CardTitle className="text-lg">{employeeData.employeeName}</CardTitle>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Building className="h-3 w-3" />
                  <span>Employee ID: {employeeData.profileId.slice(-8)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>{format(payPeriodStart, 'MMM dd')} - {format(payPeriodEnd, 'MMM dd, yyyy')}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <FileText className="h-4 w-4 mr-2" />
              {isExpanded ? 'Collapse' : 'Expand'}
            </Button>
            
            {onExport && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onExport('pdf')}
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        {/* Summary Section */}
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              Hours Summary
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-500" />
                  Regular Hours
                </span>
                <span className="font-mono">{formatHours(employeeData.calculation.calculationSummary.regularHours)}</span>
              </div>
              
              {employeeData.calculation.calculationSummary.overtimeHours > 0 && (
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-orange-500" />
                    Overtime Hours
                  </span>
                  <span className="font-mono">{formatHours(employeeData.calculation.calculationSummary.overtimeHours)}</span>
                </div>
              )}
              
              {employeeData.calculation.calculationSummary.holidayHours > 0 && (
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-green-500" />
                    Holiday Hours
                  </span>
                  <span className="font-mono">{formatHours(employeeData.calculation.calculationSummary.holidayHours)}</span>
                </div>
              )}
              
              {employeeData.calculation.calculationSummary.nonScheduledDayHours > 0 && (
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-purple-500" />
                    Non-Scheduled Day Hours
                  </span>
                  <span className="font-mono">{formatHours(employeeData.calculation.calculationSummary.nonScheduledDayHours)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              Pay Summary
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between items-center text-lg">
                <span className="font-semibold">Gross Pay</span>
                <span className="font-mono font-bold text-green-600">
                  {showDetails ? formatCurrency(employeeData.calculation.totalGrossPay) : '***.**'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span>Total Deductions</span>
                <span className="font-mono text-red-600">
                  -{showDetails ? formatCurrency(totalDeductions + totalTaxes) : '***.**'}
                </span>
              </div>
              <Separator />
              <div className="flex justify-between items-center text-xl font-bold">
                <span>Net Pay</span>
                <span className="font-mono">
                  {showDetails ? formatCurrency(employeeData.calculation.netPay) : '***.**'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Breakdown (Expanded) */}
        {isExpanded && (
          <div className="space-y-6">
            <Separator />
            
            {/* Earnings Breakdown */}
            {lineItemGroups.earnings.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-semibold text-green-700 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Earnings
                </h4>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="space-y-2">
                    {lineItemGroups.earnings.map((item, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <div>
                          <span className="font-medium">{item.description}</span>
                          {item.hours_applied > 0 && (
                            <span className="text-sm text-muted-foreground ml-2">
                              ({formatHours(item.hours_applied)} hrs @ {formatCurrency(item.rate_applied || 0)})
                            </span>
                          )}
                        </div>
                        <span className="font-mono text-green-700">
                          {showDetails ? formatCurrency(item.gross_amount) : '***.**'}
                        </span>
                      </div>
                    ))}
                    <Separator className="bg-green-200" />
                    <div className="flex justify-between items-center font-semibold">
                      <span>Total Earnings</span>
                      <span className="font-mono text-green-700">
                        {showDetails ? formatCurrency(totalEarnings) : '***.**'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Deductions Breakdown */}
            {(lineItemGroups.deductions.length > 0 || lineItemGroups.taxes.length > 0) && (
              <div className="space-y-3">
                <h4 className="font-semibold text-red-700 flex items-center gap-2">
                  <Minus className="h-4 w-4" />
                  Deductions
                </h4>
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="space-y-2">
                    {/* Regular Deductions */}
                    {lineItemGroups.deductions.map((item, index) => (
                      <div key={`deduction-${index}`} className="flex justify-between items-center">
                        <div>
                          <span className="font-medium">{item.description}</span>
                          <Badge variant="outline" className="ml-2 text-xs">
                            {item.calculation_method?.replace('_', ' ').toUpperCase()}
                          </Badge>
                        </div>
                        <span className="font-mono text-red-700">
                          -{showDetails ? formatCurrency(item.gross_amount) : '***.**'}
                        </span>
                      </div>
                    ))}
                    
                    {/* Tax Deductions */}
                    {lineItemGroups.taxes.map((item, index) => {
                      let taxDetails = null;
                      if (item.calculation_notes) {
                        try {
                          taxDetails = JSON.parse(item.calculation_notes);
                        } catch (e) {
                          // If not JSON, ignore
                        }
                      }
                      
                      return (
                        <div key={`tax-${index}`} className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{item.description}</span>
                            <Badge variant="secondary" className="ml-2 text-xs">TAX</Badge>
                            {taxDetails && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-md p-4">
                                    <div className="space-y-3 text-sm">
                                      <p className="font-semibold border-b pb-2">Tax Calculation Breakdown</p>
                                      
                                      <div className="space-y-1.5">
                                        <div className="flex justify-between gap-4">
                                          <span className="text-muted-foreground">Taxable Income (YTD):</span>
                                          <span className="font-mono">{formatCurrency(taxDetails.ytd_taxable_income || taxDetails.ytd_taxable || 0)}</span>
                                        </div>
                                        <div className="flex justify-between gap-4">
                                          <span className="text-muted-foreground">Rounded PKP:</span>
                                          <span className="font-mono">{formatCurrency(taxDetails.rounded_pkp || 0)}</span>
                                        </div>
                                        <div className="flex justify-between gap-4">
                                          <span className="text-muted-foreground">Annual Tax:</span>
                                          <span className="font-mono font-semibold">{formatCurrency(taxDetails.annual_tax_rounded || taxDetails.annual_tax || 0)}</span>
                                        </div>
                                        <div className="flex justify-between gap-4">
                                          <span className="text-muted-foreground">YTD Tax Withheld:</span>
                                          <span className="font-mono">{formatCurrency(taxDetails.ytd_withheld || 0)}</span>
                                        </div>
                                        <div className="flex justify-between gap-4">
                                          <span className="text-muted-foreground">Current Period:</span>
                                          <span className="font-mono">{formatCurrency(taxDetails.current_period_withholding || 0)}</span>
                                        </div>
                                      </div>
                                      
                                      {taxDetails.breakdown && taxDetails.breakdown.length > 0 && (
                                        <div className="pt-2 border-t">
                                          <p className="font-medium mb-2">Tax Brackets Applied:</p>
                                          <div className="space-y-1.5">
                                            {taxDetails.breakdown.map((tier: any, i: number) => (
                                              <div key={i} className="text-xs bg-muted/50 p-2 rounded">
                                                <div className="flex justify-between">
                                                  <span>{tier.rate_percent}% on {formatCurrency(tier.taxable_portion || 0)}</span>
                                                  <span className="font-mono">{formatCurrency(tier.tax_amount || 0)}</span>
                                                </div>
                                                <div className="text-muted-foreground">
                                                  Range: {formatCurrency(tier.lower_bound || 0)} - {tier.upper_bound ? formatCurrency(tier.upper_bound) : 'above'}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                          <span className="font-mono text-red-700">
                            -{showDetails ? formatCurrency(item.gross_amount) : '***.**'}
                          </span>
                        </div>
                      );
                    })}
                    
                    <Separator className="bg-red-200" />
                    <div className="flex justify-between items-center font-semibold">
                      <span>Total Deductions</span>
                      <span className="font-mono text-red-700">
                        -{showDetails ? formatCurrency(totalDeductions + totalTaxes) : '***.**'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Year-to-Date Summary */}
            {employeeData.yearToDateSummary && employeeData.yearToDateSummary.periods_count > 0 && (
              <div className="bg-muted rounded-lg p-4">
                <h4 className="font-semibold mb-3">Year-to-Date Summary</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">YTD Gross</p>
                    <p className="font-mono font-semibold">
                      {showDetails ? formatCurrency(employeeData.yearToDateSummary.total_gross) : '***.**'}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">YTD Deductions</p>
                    <p className="font-mono font-semibold text-red-600">
                      -{showDetails ? formatCurrency(employeeData.yearToDateSummary.total_gross - employeeData.yearToDateSummary.total_net) : '***.**'}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">YTD Net</p>
                    <p className="font-mono font-semibold">
                      {showDetails ? formatCurrency(employeeData.yearToDateSummary.total_net) : '***.**'}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Based on {employeeData.yearToDateSummary.periods_count} pay period(s) • {formatHours(employeeData.yearToDateSummary.total_hours)} hours
                </p>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-xs text-muted-foreground text-center pt-4 border-t">
          <p>This is a preview calculation based on current compensation rules and time entries.</p>
          <p>Generated on {format(new Date(), 'PPP')} • Subject to final payroll processing</p>
        </div>
      </CardContent>
    </Card>
  );
}